import React, { useState, useEffect } from 'react';
import { Phone, Mail, MapPin, Clock, Send, CheckCircle } from 'lucide-react';
import { useForm } from '@inertiajs/react';
import '../styles/ContactUs.css';


const ContactUs = () => {
    const { data, setData, post, processing, reset, errors, recentlySuccessful } = useForm({
        name: '',
        email: '',
        phone: '',
        subject: '',
        message: '',
        preferred_contact: 'email',
        captcha: ''
    });

    const [captchaQuestion, setCaptchaQuestion] = useState('');

    const fetchCaptcha = async () => {
        try {
            const response = await fetch(route('captcha.get'));
            const data = await response.json();
            setCaptchaQuestion(data.question);
        } catch (error) {
            console.error('Failed to fetch captcha:', error);
        }
    };

    useEffect(() => {
        fetchCaptcha();
    }, []);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setData(name, value);
    };

    const handleSubmit = (e) => {
        e.preventDefault();

        post(route('contact.store'), {
            preserveScroll: true,
            onSuccess: () => {
                reset();
                fetchCaptcha();
            },
            onError: () => {
                fetchCaptcha();
            }
        });
    };

    return (
        <section id="contact" className="contact-section">
            {/* Background Elements */}
            <div className="contact-bg-gradient"></div>
            <div className="contact-bg-pattern"></div>

            <div className="contact-container">
                {/* Header */}
                <div className="contact-header">
                    <span className="contact-label">Get In Touch</span>
                    <h2>Contact Hollywood Royale Garden</h2>
                    <p>We're here to answer your questions and help you discover your new home</p>
                </div>

                {/* Content Grid */}
                <div className="contact-content">
                    {/* Left Side - Contact Information */}
                    <div className="contact-info-wrapper">
                        <div className="contact-info-card">
                            <div className="contact-info-header">
                                <h3>Let's Connect</h3>
                                <p>Reach out to us through any of these channels</p>
                            </div>

                            <div className="contact-methods">
                                <div className="contact-method">
                                    <div className="contact-icon">
                                        <Phone size={24} />
                                    </div>
                                    <div className="contact-details">
                                        <h4>Phone</h4>
                                        <a href="tel:+13234662411">+1 323-466-2411</a>
                                        <span className="contact-note">Mon-Fri, 9am-6pm</span>
                                    </div>
                                </div>

                                <div className="contact-method">
                                    <div className="contact-icon">
                                        <Mail size={24} />
                                    </div>
                                    <div className="contact-details">
                                        <h4>Email</h4>
                                        <a href="mailto:info@hrgalla.com">info@hrgalla.com</a>
                                        <span className="contact-note">We'll respond within 24 hours</span>
                                    </div>
                                </div>

                                <div className="contact-method">
                                    <div className="contact-icon">
                                        <MapPin size={24} aria-hidden="true" />
                                    </div>
                                    <div className="contact-details">
                                        <h4>Visit Our Hollywood Location</h4>
                                        <address style={{ fontStyle: 'normal' }}>
                                            6054 Franklin Ave<br />
                                            Los Angeles, CA 90028
                                        </address>
                                        <span className="contact-note">RCFE License # 197610432</span>
                                    </div>
                                </div>

                                <div className="contact-method">
                                    <div className="contact-icon">
                                        <Clock size={24} />
                                    </div>
                                    <div className="contact-details">
                                        <h4>Office Hours</h4>
                                        <p>Monday - Friday: 9:00 AM - 5:00 PM<br />
                                            Saturday: 10:00 AM - 4:00 PM<br />
                                            Sunday: By Appointment</p>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Quick Actions */}
                        <div className="quick-actions">

                            <a href="/virtual-tour" className="quick-action-btn secondary">
                                Virtual Tour →
                            </a>
                        </div>
                    </div>

                    {/* Right Side - Contact Form */}
                    <div className="contact-form-wrapper">
                        <form className="contact-form" onSubmit={handleSubmit}>
                            <div className="form-header">
                                <h3>Send Us a Message</h3>
                                <p>Fill out the form below and we'll get back to you shortly</p>
                            </div>

                            <div className="form-grid">
                                <div className="form-group">
                                    <label htmlFor="name">Full Name *</label>
                                    <input
                                        type="text"
                                        id="name"
                                        name="name"
                                        value={data.name}
                                        onChange={handleChange}
                                        required
                                        placeholder="John Doe"
                                    />
                                    {errors.name && <span className="error-text">{errors.name}</span>}
                                </div>

                                <div className="form-group">
                                    <label htmlFor="email">Email Address *</label>
                                    <input
                                        type="email"
                                        id="email"
                                        name="email"
                                        value={data.email}
                                        onChange={handleChange}
                                        required
                                        placeholder="john@example.com"
                                    />
                                    {errors.email && <span className="error-text">{errors.email}</span>}
                                </div>

                                <div className="form-group">
                                    <label htmlFor="phone">Phone Number</label>
                                    <input
                                        type="tel"
                                        id="phone"
                                        name="phone"
                                        value={data.phone}
                                        onChange={handleChange}
                                        placeholder="(555) 123-4567"
                                    />
                                    {errors.phone && <span className="error-text">{errors.phone}</span>}
                                </div>

                                <div className="form-group">
                                    <label htmlFor="subject">Subject *</label>
                                    <select
                                        id="subject"
                                        name="subject"
                                        value={data.subject}
                                        onChange={handleChange}
                                        required
                                    >
                                        <option value="">Select a subject</option>
                                        <option value="Schedule a Tour">Schedule a Tour</option>
                                        <option value="Room Availability">Room Availability</option>
                                        <option value="Care Services Inquiry">Care Services Inquiry</option>
                                        <option value="General Question">General Question</option>
                                        <option value="Other">Other</option>
                                    </select>
                                    {errors.subject && <span className="error-text">{errors.subject}</span>}
                                </div>
                            </div>

                            <div className="form-group full-width">
                                <label htmlFor="message">Message *</label>
                                <textarea
                                    id="message"
                                    name="message"
                                    value={data.message}
                                    onChange={handleChange}
                                    required
                                    rows="6"
                                    placeholder="Tell us how we can help you..."
                                ></textarea>
                                {errors.message && <span className="error-text">{errors.message}</span>}
                            </div>

                            <div className="form-group full-width">
                                <label>Preferred Contact Method</label>
                                <div className="radio-group">
                                    <label className="radio-label">
                                        <input
                                            type="radio"
                                            name="preferred_contact"
                                            value="email"
                                            checked={data.preferred_contact === 'email'}
                                            onChange={handleChange}
                                        />
                                        <span>Email</span>
                                    </label>
                                    <label className="radio-label">
                                        <input
                                            type="radio"
                                            name="preferred_contact"
                                            value="phone"
                                            checked={data.preferred_contact === 'phone'}
                                            onChange={handleChange}
                                        />
                                        <span>Phone</span>
                                    </label>
                                </div>
                            </div>

                            <div className="form-group full-width">
                                <label htmlFor="captcha">Spam Protection: What is {captchaQuestion}? *</label>
                                <div style={{ display: 'flex', gap: '15px', alignItems: 'center' }}>
                                    <input
                                        type="text"
                                        id="captcha"
                                        name="captcha"
                                        value={data.captcha}
                                        onChange={handleChange}
                                        required
                                        placeholder="Enter answer"
                                        style={{ maxWidth: '200px' }}
                                    />
                                    <button
                                        type="button"
                                        onClick={fetchCaptcha}
                                        className="refresh-captcha-btn"
                                        style={{
                                            background: 'none',
                                            border: 'none',
                                            color: '#d4a575',
                                            cursor: 'pointer',
                                            fontSize: '14px',
                                            textDecoration: 'underline'
                                        }}
                                    >
                                        Refresh Question
                                    </button>
                                </div>
                                {errors.captcha && <span className="error-text">{errors.captcha}</span>}
                            </div>


                            <button type="submit" className="submit-btn" disabled={recentlySuccessful || processing}>
                                {recentlySuccessful ? (
                                    <>
                                        <CheckCircle size={20} />
                                        Message Sent!
                                    </>
                                ) : processing ? (
                                    'Sending...'
                                ) : (
                                    <>
                                        <Send size={20} />
                                        Send Message
                                    </>
                                )}
                            </button>

                            {recentlySuccessful && (
                                <div className="success-message">
                                    Thank you for reaching out! We'll get back to you soon.
                                </div>
                            )}
                        </form>
                    </div>
                </div>

                {/* Map Section */}
                <div className="contact-map-section">
                    <div className="map-wrapper">
                        <iframe
                            src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3304.8234567890123!2d-118.3!3d34.1!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zMzTCsDA2JzAwLjAiTiAxMTjCsDE4JzAwLjAiVw!5e0!3m2!1sen!2sus!4v1234567890123!5m2!1sen!2sus"
                            width="100%"
                            height="100%"
                            style={{ border: 0 }}
                            allowFullScreen=""
                            loading="lazy"
                            referrerPolicy="no-referrer-when-downgrade"
                            title="Hollywood Royale Garden Location"
                        ></iframe>
                    </div>
                </div>
            </div>
        </section>
    );
};

export default ContactUs;
